// Authenticated HTTP stays at Gab's origin. The extension receives only a
// short-lived connection ID and task-scoped commands, never login credentials.
//
// Backend commands expire 25s after dispatch (extension_work timeoutMs) and
// /poll redelivers the pending command until a result lands. The client
// therefore keeps polling while a command is in flight — dispatching each
// command id to the extension once — and gives up loudly once the backend
// can no longer accept the result, instead of stalling silently forever.
const RESULT_TIMEOUT_MS = 30000;

export function createBrowserWorkClient({ request, send, conversationId, current = () => true, schedule = setTimeout, cancel = clearTimeout, now = Date.now }) {
  let active = true, connection, timer, dispatchedId = null, dispatchedAt = 0, completedId = null;
  const valid = () => active && current();
  // fetchWithAuth owns JSON serialization. Passing a JSON string here encodes
  // it a second time, so the backend never receives the conversation/lease.
  const post = (action, body = {}) => request(`/json/extension/work/${action}`, { method: 'POST', networkRetries: 0,
    body: { conversationId, ...body }, headers: { 'Content-Type': 'application/json' }, timeoutMs: 10000 });
  const stop = () => {
    active = false; cancel(timer);
    const id = connection; connection = undefined;
    if (id) void post('disconnect', { id }).catch(() => {});
  };
  const poll = async () => {
    if (!valid()) return;
    try {
      const result = await post('poll', { id: connection });
      if (!valid()) return;
      if (result.command && result.command.id !== dispatchedId && result.command.id !== completedId) {
        dispatchedId = result.command.id; dispatchedAt = now();
        send('work-command', { command: result.command });
      } else if (dispatchedId && now() - dispatchedAt > RESULT_TIMEOUT_MS) {
        send('work-error', { message: 'The browser did not return a result. Inspect the browser before retrying, then connect again.' });
        stop(); return;
      }
      timer = schedule(poll, 1000);
    } catch (e) { if (valid()) { send('work-error', { message: e.message || 'Work disconnected. Connect again to continue.' }); stop(); } }
  };
  return {
    async start() {
      try {
        const result = await post('connect'); connection = result.id;
        if (!valid()) { stop(); return; }
        send('work-connected', { id: connection }); void poll();
      } catch (e) { if (valid()) send('work-error', { message: e.message || 'Could not connect Work.' }); }
    },
    async result(data) {
      if (!valid() || !dispatchedId || data.connectionId !== connection || data.id !== dispatchedId) return;
      try { await post('result', { id: connection, operationId: data.id, result: data.result }); }
      catch (e) { if (valid()) { send('work-error', { message: e.message || 'Browser result could not be delivered. Inspect before retrying.' }); stop(); } }
      finally { completedId = dispatchedId; dispatchedId = null; if (valid()) { cancel(timer); timer = schedule(poll, 1000); } }
    },
    stop,
  };
}
